/**
 * WP-06C-B — The Loop inspector renders hosted authority and requests changes.
 *
 * Nothing in this component mutates a Loop locally. Every change is an
 * authenticated lifecycle command carrying the `recordVersion` that came with
 * the last authoritative read; the screen only updates after the backend has
 * spoken and the query cache has been re-read. Status options are limited to the
 * transitions the governed matrix permits, and resolution goes through the
 * confirmation dialog because the backend refuses to close a Loop whose
 * conditions are unverified.
 *
 * Failures are shown as the categorical sentences from the product message
 * vocabulary — never a provider message, SQL text, or raw exception.
 */

import { useEffect, useId, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  ArrowUpRight,
  CalendarClock,
  ChevronDown,
  Link2,
  PenLine,
  ShieldCheck,
  User2,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { HostedEvidencePanel } from "./HostedEvidencePanel";
import { LoopWatchPanel } from "./LoopWatchPanel";
import { toast } from "sonner";
import { HostedResolutionDialog } from "./HostedResolutionDialog";
import { LoopStatusBadge } from "./LoopStatusBadge";
import { StatusReasonDialog } from "./StatusReasonDialog";
import { formatLongDate, isOverdue, relativeDay } from "@/lib/looseend/format";
import { STATUS_META } from "@/lib/looseend/status";
import { dependencyLoops, whyNow } from "@/lib/looseend/selectors";
import { draftFollowUp } from "@/lib/looseend/assistant";
import { analytics } from "@/lib/analytics";
import type { Loop, LoopStatus } from "@/lib/looseend/types";
import { loopInsight, PEE_ARIA } from "@/lib/looseend/pee";
import { allowedTransitions } from "@/lib/loops/lifecycle/lifecycleCommand";
import { useHostedLoopActions } from "@/lib/loops/product/hostedLoops";
import { evidenceVerificationFailureMessage } from "@/lib/loops/product/loopMessages";
import { runResolutionSequence } from "@/lib/loops/product/resolutionSequence";
import type { HostedLoopCondition } from "@/lib/loops/read/loopProjection";
import { PeeInsight } from "./PeeInsight";
import { cn } from "@/lib/utils";

/** Statuses a person may request directly; `resolved`/`archived` have their own paths. */
const REQUESTABLE: readonly LoopStatus[] = [
  "open",
  "waiting",
  "action_needed",
  "blocked",
  "ready_to_verify",
];

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="py-2">
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-sm leading-relaxed text-foreground">{value}</dd>
    </div>
  );
}

/** A situation line: rendered only when it helps decide what to do next. */
function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="py-2.5">
      <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-0.5 text-sm leading-relaxed text-foreground">{children}</dd>
    </div>
  );
}

/** Plain-language timing: only when a date exists, and overdue said outright. */
function whenLine(loop: Loop): string | null {
  if (!loop.expectedDate) return null;
  if (isOverdue(loop)) return `Overdue · was expected ${formatLongDate(loop.expectedDate)}`;
  return `${relativeDay(loop.expectedDate)} · ${formatLongDate(loop.expectedDate)}`;
}

export function LoopInspector({
  loop,
  allLoops,
  conditions = [],
  onClose,
  variant = "panel",
  initialAction = null,
}: {
  loop: Loop;
  allLoops: Loop[];
  /** The governed resolution conditions from the authoritative detail read. */
  conditions?: readonly HostedLoopCondition[];
  onClose: () => void;
  variant?: "panel" | "sheet";
  /** Deep-link intent, e.g. arriving from a notification. */
  initialAction?: "verify" | "evidence" | null;
}) {
  const actions = useHostedLoopActions();
  const [verifying, setVerifying] = useState(false);
  /** The status awaiting its governed reason, if any. */
  const [reasonFor, setReasonFor] = useState<LoopStatus | null>(null);
  const [tab, setTab] = useState(initialAction === "evidence" ? "conditions" : "overview");
  /**
   * The newest version the AUTHORITY reported in this session. A command issued
   * immediately after another (resolve → archive) uses it so the sequence never
   * sends a version the browser already knows is stale, and never invents one.
   */
  const [authorityVersion, setAuthorityVersion] = useState<number | null>(null);
  /** Presentation only: the full domain structure is available, not imposed. */
  const [showDetails, setShowDetails] = useState(false);
  const detailsId = useId();

  useEffect(() => {
    if (initialAction === "verify") setVerifying(true);
    if (initialAction === "evidence") setTab("conditions");
  }, [initialAction]);
  const deps = dependencyLoops(loop, allLoops);
  const insight = loopInsight(loop, allLoops);
  const permitted = allowedTransitions(loop.status);
  const statusOptions = REQUESTABLE.filter((s) => s === loop.status || permitted.includes(s));
  const canResolve = permitted.includes("resolved");
  const canArchive = permitted.includes("archived");
  /** The freshest authoritative version available: a re-read, or the last reply. */
  const expectedVersion =
    authorityVersion !== null && authorityVersion > loop.recordVersion
      ? authorityVersion
      : loop.recordVersion;

  /** One place where an outcome becomes user-visible copy. */
  const announce = (
    result: { ok: true; replayed: boolean } | { ok: false; message: string; retryable: boolean },
    success: string,
    description?: string,
  ) => {
    if (result.ok) toast.success(success, description ? { description } : undefined);
    else toast.error(result.message);
  };

  const requestStatus = async (next: LoopStatus, reason?: string) => {
    const result = await actions.transition(loop, next, reason);
    announce(result, `Now marked “${STATUS_META[next].label}”`);
  };

  /**
   * `waiting` and `blocked` are refused without a reason, so the reason is
   * collected before the command is ever issued rather than after a refusal.
   */
  const chooseStatus = (next: LoopStatus) => {
    if (next === "waiting" || next === "blocked") {
      setReasonFor(next);
      return;
    }
    void requestStatus(next);
  };

  const confirmResolution = async (conditionIds: readonly string[]) => {
    const outcome = await runResolutionSequence({
      conditions: conditions.map((c) => ({
        conditionId: c.conditionId,
        verified: c.verificationState === "verified",
      })),
      attestedIds: conditionIds,
      expectedVersion,
      verifyCondition: (conditionId, recordVersion) =>
        actions.verifyCondition({ id: loop.id, recordVersion }, conditionId, "human_attestation"),
      resolve: (recordVersion) => actions.resolve({ id: loop.id, recordVersion }),
    });
    if (!outcome.ok) {
      toast.error(outcome.message);
      return;
    }
    // The version the resolve returned is what a following archive must expect.
    setAuthorityVersion(outcome.recordVersion);
    analytics.track("loop_closed", { interface_surface: "inspector", success: true });
    setVerifying(false);
    toast.success("Resolved.", { description: "The outcome is confirmed and recorded." });
  };

  /**
   * WP-A-UI — Evidence-backed verification of ONE condition. The backend
   * (`verify_condition` with method `evidence`) re-decides eligibility through
   * `evidence_supports_condition`; nothing is marked verified here. The version
   * the authority returns becomes the expectation for the following resolve.
   */
  const verifyWithEvidence = async (evidenceId: string, conditionId: string) => {
    const result = await actions.verifyCondition(
      { id: loop.id, recordVersion: expectedVersion },
      conditionId,
      "evidence",
      evidenceId,
    );
    if (result.ok) {
      setAuthorityVersion(result.recordVersion);
      analytics.track("resolution_verification_started", {
        interface_surface: "inspector",
        loop_status: loop.status,
      });
      return result;
    }
    return {
      ...result,
      message: result.reason ? evidenceVerificationFailureMessage(result.reason) : result.message,
    };
  };

  const takeAction = () => {
    if (loop.status === "ready_to_verify" || canResolve) {
      analytics.track("resolution_verification_started", {
        interface_surface: "inspector",
        loop_status: loop.status,
      });
      setVerifying(true);
      return;
    }
    if (loop.nextAction.kind === "send_reminder" || loop.nextAction.kind === "draft_message") {
      void navigator.clipboard?.writeText(draftFollowUp(loop)).catch(() => undefined);
      toast.success("Follow-up drafted", {
        description: "The draft is on your clipboard, ready to send.",
      });
      return;
    }
    if (loop.nextAction.kind === "wait") {
      toast.info("Nothing needed yet", { description: loop.nextAction.detail });
      return;
    }
    if (permitted.includes("ready_to_verify")) {
      void requestStatus("ready_to_verify");
      return;
    }
    toast.info("Nothing to do here yet", { description: loop.nextAction.detail });
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <header className="flex items-start gap-1 border-b border-border px-4 py-4 sm:px-5">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <LoopStatusBadge status={loop.status} size="sm" />
            {loop.resolutionCycle > 1 ? (
              <span
                data-testid="loop-resolution-cycle"
                className="rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground"
                title="This was reopened. Earlier history is kept."
              >
                Cycle {loop.resolutionCycle}
              </span>
            ) : null}
          </div>
          <h2 className="mt-2 text-base font-semibold leading-snug text-foreground">
            {loop.title}
          </h2>
        </div>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Close Loop inspector"
          onClick={onClose}
          className="shrink-0"
        >
          <X className="size-4" aria-hidden="true" />
        </Button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-5">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full">
            <TabsTrigger value="overview">Where this stands</TabsTrigger>
            <TabsTrigger value="conditions">Finish it</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4">
            {/* 1 — What's next: the strongest visual priority on the page. */}
            {loop.status === "resolved" || loop.status === "archived" ? (
              <div className="rounded-xl border border-border bg-surface p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {loop.status === "resolved" ? "Resolved" : "Set aside"}
                </p>
                <p className="mt-1.5 text-sm text-foreground">
                  {loop.status === "resolved"
                    ? "Nothing more is needed unless circumstances change."
                    : "Kept for the record. Nothing more is needed."}
                </p>
              </div>
            ) : (
              <div className="rounded-xl border border-primary/20 bg-primary-soft p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                  What&rsquo;s next
                </p>
                <p className="mt-1.5 text-sm font-medium text-foreground">
                  {loop.nextAction.label}
                </p>
                <p className="mt-1 text-sm text-muted-foreground">{loop.nextAction.detail}</p>
              </div>
            )}

            {/* 2–4 — Timing, who it's waiting on, and what closes it: only when meaningful. */}
            <dl className="mt-2 divide-y divide-border/70">
              {whenLine(loop) ? (
                <Fact label="When">
                  <span className={cn(isOverdue(loop) && "font-medium text-status-action")}>
                    {whenLine(loop)}
                  </span>
                </Fact>
              ) : null}
              {loop.counterparty && (loop.status === "waiting" || loop.status === "blocked") ? (
                <Fact label="Waiting on">{loop.counterparty}</Fact>
              ) : null}
              <Fact label="Resolved when">{loop.resolutionCondition.statement}</Fact>
            </dl>

            {insight ? <PeeInsight insight={insight} className="mt-3" /> : null}

            {/* 5 — Everything the system knows, available on request. */}
            <button
              type="button"
              className="mt-2 inline-flex min-h-9 items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
              aria-expanded={showDetails}
              aria-controls={detailsId}
              onClick={() => setShowDetails((d) => !d)}
            >
              <ChevronDown
                className={cn("size-4 transition-transform", showDetails && "rotate-180")}
                aria-hidden="true"
              />
              {showDetails ? "Hide details" : "Details"}
            </button>

            {showDetails ? (
              <div id={detailsId} className="mt-1 border-t border-border/70 pt-3">
                {loop.summary ? (
                  <p className="text-sm leading-relaxed text-muted-foreground">{loop.summary}</p>
                ) : null}

                <div className="mt-3 rounded-xl border border-border bg-surface p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Why <span aria-label={PEE_ARIA}>PEE™</span> surfaced this
                  </p>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground">
                    {whyNow(loop, allLoops)}
                  </p>
                </div>

                <div className="mt-3 space-y-2">
                  <label
                    className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
                    htmlFor="inspector-status"
                  >
                    Status
                  </label>
                  <Select
                    value={loop.status}
                    disabled={actions.busy || statusOptions.length <= 1}
                    onValueChange={(v) => chooseStatus(v as LoopStatus)}
                  >
                    <SelectTrigger id="inspector-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statusOptions.map((s) => (
                        <SelectItem key={s} value={s}>
                          {STATUS_META[s].label}
                        </SelectItem>
                      ))}
                      {loop.status === "resolved" || loop.status === "archived" ? (
                        <SelectItem value={loop.status}>
                          {STATUS_META[loop.status].label}
                        </SelectItem>
                      ) : null}
                    </SelectContent>
                  </Select>
                </div>

                <dl className="mt-3 divide-y divide-border">
                  <Field label="Objective" value={loop.objective} />
                  <Field label="Current state" value={STATUS_META[loop.status].description} />
                  <Field
                    label="Responsible party"
                    value={loop.counterparty ?? "No one else involved"}
                  />
                  <Field label="Expected event" value={loop.expectedEvent} />
                  <Field
                    label="Expected date"
                    value={
                      loop.expectedDate
                        ? `${formatLongDate(loop.expectedDate)} · ${relativeDay(loop.expectedDate)}`
                        : "No date set"
                    }
                  />
                  {loop.resolutionCycle > 1 ? (
                    <Field
                      label="Resolution cycle"
                      value={`Cycle ${loop.resolutionCycle} — this was reopened; earlier history is kept.`}
                    />
                  ) : null}
                </dl>

                <div className="mt-4">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Dependencies
                  </p>
                  {deps.length ? (
                    <ul className="mt-2 space-y-2">
                      {deps.map(({ dependency, loop: other }) => (
                        <li key={dependency.loopId} className="rounded-xl border border-border p-3">
                          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <Link2 className="size-3.5" aria-hidden="true" />
                            {dependency.relation === "blocked_by" ? "Blocked by" : "Blocks"}
                          </p>
                          <p className="mt-1 text-sm font-medium text-foreground">{other.title}</p>
                          <p className="text-sm text-muted-foreground">{dependency.note}</p>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-2 text-sm text-muted-foreground">
                      Nothing else depends on this Loop.
                    </p>
                  )}
                </div>
              </div>
            ) : null}

            <LoopWatchPanel loop={loop} />
          </TabsContent>

          <TabsContent value="conditions" className="mt-4">
            <p className="mb-3 text-sm text-muted-foreground">
              This counts as finished when the following is confirmed.
            </p>
            {conditions.length ? (
              <ul className="space-y-2.5">
                {conditions.map((condition) => {
                  const verified = condition.verificationState === "verified";
                  return (
                    <li
                      key={condition.conditionId}
                      className="rounded-xl border border-border p-3"
                      data-testid="loop-condition"
                      data-verified={verified ? "true" : "false"}
                    >
                      <p className="text-sm leading-relaxed text-foreground">
                        {condition.statement}
                      </p>
                      <p
                        className={cn(
                          "mt-1.5 text-xs",
                          verified ? "text-status-resolved" : "text-muted-foreground",
                        )}
                      >
                        {verified
                          ? "Confirmed"
                          : condition.requirement === "required"
                            ? "Still needs confirmation"
                            : "Optional — not yet confirmed"}
                        {!verified && condition.requiresEvidence ? " · needs proof" : ""}
                      </p>
                      {!verified && condition.requiresEvidence ? (
                        <p className="mt-1 text-xs text-muted-foreground">
                          Add something below that shows this happened, link it here, then confirm
                          with it.
                        </p>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">
                Nothing has been recorded yet about what would finish this.
              </p>
            )}

            {/* Governed evidence, read and mutated only through the backend. */}
            <div className="mt-5 border-t border-border pt-4">
              <h3 className="text-sm font-medium text-foreground">Evidence</h3>
              <p className="mb-2 text-xs text-muted-foreground">
                Add something that shows this happened — a receipt, confirmation email, screenshot
                or document.
              </p>
              <HostedEvidencePanel
                loopId={loop.id}
                conditions={conditions}
                verifyBusy={actions.busy}
                onVerifyWithEvidence={
                  loop.status === "resolved" || loop.status === "archived"
                    ? undefined
                    : verifyWithEvidence
                }
              />
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <footer className="grid gap-2 border-t border-border px-4 py-3 sm:px-5">
        <div className="flex flex-wrap gap-2">
          {loop.status === "resolved" ? (
            <Button
              className="flex-1"
              variant="outline"
              data-testid="loop-reopen"
              disabled={actions.busy}
              onClick={async () => {
                // WP-06R: the dedicated reopen operation. The authority alone
                // starts the next resolution cycle and resets condition state;
                // prior proof is kept as history, never rewritten.
                const result = await actions.reopen({
                  id: loop.id,
                  recordVersion: expectedVersion,
                });
                if (result.ok) {
                  setAuthorityVersion(result.recordVersion);
                  analytics.track("loop_reopened", { interface_surface: "inspector" });
                }
                announce(
                  result,
                  "This is active again.",
                  "Previous proof has been kept. Anything required to finish it must be confirmed again.",
                );
              }}
            >
              This isn’t finished — reopen
            </Button>
          ) : (
            <Button
              className="flex-1"
              data-testid="loop-take-action"
              disabled={actions.busy}
              onClick={takeAction}
            >
              {loop.status === "ready_to_verify" ? (
                <ShieldCheck className="size-4" aria-hidden="true" />
              ) : (
                <PenLine className="size-4" aria-hidden="true" />
              )}
              {loop.status === "ready_to_verify" || canResolve
                ? "Confirm this is finished"
                : loop.nextAction.kind === "wait"
                  ? "Nothing needed yet"
                  : "Take the next step"}
            </Button>
          )}
          <Button variant="outline" asChild>
            <Link to="/app/loops/$loopId" params={{ loopId: loop.id }}>
              <ArrowUpRight className="size-4" aria-hidden="true" />
              {variant === "sheet" ? "Full Loop" : "Open full details"}
            </Link>
          </Button>
          {canArchive ? (
            <Button
              variant="ghost"
              data-testid="loop-archive"
              disabled={actions.busy}
              onClick={async () => {
                // Archive expects the version the authority last reported, which
                // after a resolve is the version the resolve itself returned.
                const result = await actions.archive({
                  id: loop.id,
                  recordVersion: expectedVersion,
                });
                if (result.ok) setAuthorityVersion(result.recordVersion);
                announce(result, "Archived", "It is kept for the record and no longer surfaced.");
              }}
            >
              Archive
            </Button>
          ) : null}
        </div>

        {loop.counterparty || loop.expectedDate ? (
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            {loop.counterparty ? (
              <span className="inline-flex items-center gap-1">
                <User2 className="size-3.5" aria-hidden="true" />
                {loop.counterparty}
              </span>
            ) : null}
            {loop.expectedDate ? (
              <span className="ml-auto inline-flex items-center gap-1">
                <CalendarClock className="size-3.5" aria-hidden="true" />
                {isOverdue(loop) ? "Overdue" : relativeDay(loop.expectedDate)}
              </span>
            ) : null}
          </p>
        ) : null}
      </footer>

      <HostedResolutionDialog
        open={verifying}
        onOpenChange={setVerifying}
        conditions={conditions}
        busy={actions.busy}
        onConfirm={(ids) => void confirmResolution(ids)}
        onGoToEvidence={() => {
          setVerifying(false);
          setTab("conditions");
        }}
      />

      <StatusReasonDialog
        open={reasonFor !== null}
        statusLabel={reasonFor ? STATUS_META[reasonFor].label : ""}
        busy={actions.busy}
        onCancel={() => setReasonFor(null)}
        onConfirm={(reason) => {
          const target = reasonFor;
          setReasonFor(null);
          if (target) void requestStatus(target, reason);
        }}
      />
    </div>
  );
}
